import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { AssistantRequest, PendingAIRequest } from 'ai/types';
import useAskAssistant from 'ai/hooks/useAskAssistant';
import useDisclosure from 'util/hooks/useDisclosure';
import { useInvalidateAIQuotaStatus } from 'ai/hooks/useAIQuotaStatus';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import { getPathnameWithoutId } from 'util/URLUtils';
import useLocation from 'routing/useLocation';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';

interface AssistantSidebarContextType {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
  generate: (request: AssistantRequest) => void;
  pendingRequest: PendingAIRequest | null;
}

export const AssistantSidebarContext = React.createContext<AssistantSidebarContextType | undefined>(undefined);

export const AssistantSidebarProvider = ({ children }: React.PropsWithChildren) => {
  const [isOpen, { open, close, toggle }] = useDisclosure();
  const [pendingRequest, setPendingRequest] = useState<PendingAIRequest | null>(null);
  const { request } = useAskAssistant();
  const refetchQuota = useInvalidateAIQuotaStatus();
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();

  const startPendingRequest = useCallback(
    (r: AssistantRequest) => setPendingRequest({ isPending: true, request: r }),
    [],
  );
  const clearPendingRequest = useCallback(() => setPendingRequest(null), []);
  const setPendingError = useCallback(
    (error: string, retry: () => void) =>
      setPendingRequest((current) => ({ isPending: false, error, retry, request: current.request })),
    [],
  );

  const generate = useCallback(
    (r: AssistantRequest) => {
      const mutation = () => request(r).then(clearPendingRequest).then(refetchQuota);

      startPendingRequest(r);
      mutation().then(
        () => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.WIDGET_SUMMARY.REQUEST_SUCCESS, {
            app_pathname: getPathnameWithoutId(pathname),
          });
        },
        (error) => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.WIDGET_SUMMARY.REQUEST_FAILURE, {
            app_pathname: getPathnameWithoutId(pathname),
          });

          setPendingError(error.message, () => generate(r));
        },
      );
      open();
    },
    [clearPendingRequest, open, pathname, refetchQuota, request, sendTelemetry, setPendingError, startPendingRequest],
  );

  const value = useMemo(
    () => ({ isOpen, toggle, open, close, generate, pendingRequest }),
    [isOpen, toggle, open, close, generate, pendingRequest],
  );

  return <AssistantSidebarContext.Provider value={value}>{children}</AssistantSidebarContext.Provider>;
};
